/*
 * "Buy Now" button (snippets/buy-buttons.liquid).
 *
 * Adds the form's selected variant to the cart and sends the customer straight
 * to checkout. The handler is delegated from `document` and bound once, so it
 * also covers product forms injected after page load, such as the "Choose
 * options" quick-add modal on collection pages, where a DOMContentLoaded
 * listener would never run.
 */
(function () {
  if (window.loopsBuyNowBound) return;
  window.loopsBuyNowBound = true;

  const BUTTON_SELECTOR = '.main-product-form-buy-now';
  const pendingButtons = new WeakSet();
  const checkoutRedirects = new Map();

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  function liveRegion() {
    return (
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText')
    );
  }

  function announce(text, clearAfter) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = text;
    region.setAttribute('aria-hidden', false);
    if (clearAfter) {
      setTimeout(() => {
        region.setAttribute('aria-hidden', true);
        region.textContent = '';
      }, clearAfter);
    }
  }

  function setLoading(button, loading) {
    if (loading) pendingButtons.add(button);
    else pendingButtons.delete(button);
    const addToCart = button.closest('form')?.querySelector('[type="submit"][name="add"]');
    button.disabled = loading || button.getAttribute('aria-disabled') === 'true' || Boolean(addToCart?.disabled);
    button.classList.toggle('loading', loading);
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
    if (loading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function onClick(event) {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    if (pendingButtons.has(button) || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const form = button.closest('form');
    const variantId = form && form.querySelector('input[name="id"]')?.value;
    if (!variantId) {
      console.error('Buy Now: variant ID not found');
      return;
    }

    // Mirror the Add to Cart button: when the selected variant is sold out or
    // unavailable, that button is disabled and so is Buy Now.
    const addToCart = form.querySelector('[type="submit"][name="add"]');
    if (addToCart && (addToCart.hasAttribute('disabled') || addToCart.getAttribute('aria-disabled') === 'true')) return;

    // Buy Now is not a submit button, so the browser does not run the form's
    // constraint validation for it. Run it here so a required field such as
    // the gift card recipient email is flagged and focused, as Add to Cart does.
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const productForm = button.closest('product-form');
    const recipientForm = productForm?.querySelector('recipient-form');
    productForm?.handleErrorMessage?.(false, true);
    recipientForm?.clearErrorMessage?.();

    // FormData carries the variant, quantity, selling plan and line item
    // properties exactly as Add to Cart would send them.
    const body = new FormData(form);
    body.delete('sections');
    body.delete('sections_url');

    setLoading(button, true);

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body,
    })
      .then((response) =>
        response.json().then((payload) => {
          if (!response.ok || payload.status) {
            // The cart API explains the failure (sold out, recipient email...).
            const error = new Error(payload.description || payload.message || `Error adding to cart (${response.status})`);
            error.details = payload.errors;
            throw error;
          }
          return payload;
        })
      )
      .then((item) => {
        const title = item.product_title || document.querySelector('h1.product__title')?.textContent.trim() || 'Item';
        let text = `${title} added to cart`;
        if (item.final_line_price) text += `. ${formatMoney(item.final_line_price)}`;
        text += ', redirecting to checkout';
        announce(text);

        // Give screen readers a moment to read the announcement before leaving.
        checkoutRedirects.set(
          button,
          setTimeout(() => {
            window.location = `${window.Shopify?.routes?.root || '/'}checkout`;
          }, 500)
        );
      })
      .catch((error) => {
        console.error('Buy Now:', error);
        setLoading(button, false);
        const message = error.message || 'Error adding item to cart. Please try again.';
        // Gift-card forms hide the standard error box and render recipient
        // errors separately. Keep the error scoped to the form that submitted.
        if (recipientForm && typeof recipientForm.displayErrorMessage === 'function') {
          recipientForm.displayErrorMessage(message, error.details || message);
        } else if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(message, true);
        }
        announce(message, 5000);
      });
  }

  document.addEventListener('click', onClick);

  // Browser Back can restore the entire page, including the checkout spinner
  // and request lock. Reset completed cart requests, but keep real pending requests
  // locked. Cancel any redirect timer that was frozen when the page was cached.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    checkoutRedirects.forEach((timer, button) => {
      clearTimeout(timer);
      setLoading(button, false);
    });
    checkoutRedirects.clear();
  });

  // Keep Buy Now in step with variant availability. product-info.js publishes
  // this after every variant change, for the main product and the quick-add
  // modal alike.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data) return;
      document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
        const productInfo = button.closest('product-info');
        if (!productInfo) return;
        const { section, originalSection } = productInfo.dataset;
        if (section !== data.sectionId && originalSection !== data.sectionId) return;
        // Mirror the re-rendered Add to Cart button: its Liquid already accounts
        // for inventory and quantity rules, and product-info.js reads the same
        // element to update Add to Cart. Fall back to plain availability if the
        // re-rendered markup is missing. Keep `disabled` and `aria-disabled` in
        // step, since the snippet renders both and the click handler honours both.
        const addToCart = data.html && data.html.getElementById(`ProductSubmitButton-${data.sectionId}`);
        const disabled = addToCart
          ? addToCart.hasAttribute('disabled') || addToCart.getAttribute('aria-disabled') === 'true'
          : !(data.variant && data.variant.available);
        // Availability updates must not release an in-flight checkout request.
        button.disabled = disabled || pendingButtons.has(button);
        if (disabled) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    });
  }
})();
